import { useMutation, useQuery } from '@apollo/client'
import { TypedFormik } from '@dai/components'
import { CheckCircle, Close, Info } from '@mui/icons-material'
import {
  Alert,
  Button,
  ButtonProps,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import * as Yup from 'yup'

import { Colors } from '@/components/Colors'
import CopyableField from '@/components/common/CopyableField'
import { CommonFilters } from '@/components/filters/filters.helpers'
import {
  CREATE_USER,
  CreateUserMutation,
  CreateUserMutationVariables,
  GET_UNIQUE_PIN_NUMBER,
  GetUniquePinNumberQuery,
  GetUniquePinNumberQueryVariables,
} from '@/graphql'
import useDaiGroups from '@/hooks/use-dai-groups'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'
import { RemoteSearchableSelect } from '@/pages/components/RemoteSearchableSelect'
import { LocalizationHelpers } from '@/utils/localization.helpers'

import PinInputField from './PinInputField'

function CreateUser({
  onCancel,
  onSuccess,
  otherButtons,
}: {
  onSuccess?: () => void
  onCancel?: () => void
  otherButtons?: ButtonProps[]
}) {
  const { user } = useLoggedInUser()
  const { t } = useT()
  const [selectedStores, setSelectedStores] = useState<
    { label: string; value: string }[]
  >([])
  const [formPin, setFormPin] = useState<string | null>(null)
  const [isPinValid, setIsPinValid] = useState<boolean>(false)
  const [createdUserInfo, setCreatedUserInfo] = useState<{
    username: string
    pin: string
  } | null>(null)

  const handleReset = () => {
    setSelectedStores([])
    setFormPin('')
    setIsPinValid(false)
  }

  const [createUser] = useMutation<
    CreateUserMutation,
    CreateUserMutationVariables
  >(CREATE_USER)

  const pinData = useQuery<
    GetUniquePinNumberQuery,
    GetUniquePinNumberQueryVariables
  >(GET_UNIQUE_PIN_NUMBER, {
    fetchPolicy: 'network-only',
  })
  const uniquePin = pinData.data?.uniquePinNumber ?? ''

  const {
    groups,
    adminGroup,
    loading: groupsLoading,
    error: groupsError,
  } = useDaiGroups()

  return (
    <TypedFormik
      initialValues={{
        firstName: '',
        lastName: '',
        username: '',
        jobTitle: '',
        groupIds: '',
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().required(
          t('createUserForm.validation.firstNameRequired'),
        ),
        lastName: Yup.string().required(
          t('createUserForm.validation.lastNameRequired'),
        ),
        username: Yup.string().required(
          t('createUserForm.validation.usernameRequired'),
        ),
        groupIds: Yup.string().required(
          t(
            'createUserForm.validation.roleRequired',
            'User must have an assigned role',
          ),
        ),
      })}
      onSubmit={async (values, { setStatus, resetForm }) => {
        try {
          if (!user) throw new Error(t('inviteUser.error.userNotFound'))
          if (!user.tenant)
            throw new Error(t('inviteUser.error.tenantNotFound'))

          await createUser({
            variables: {
              data: {
                tenantId: user.tenant.id,
                firstName: values.firstName,
                lastName: values.lastName,
                jobTitle: values.jobTitle,
                groupIds: [values.groupIds],
                storeIds: selectedStores.map(store => store.value),
                username: values.username,
                pinNumber: formPin,
              },
            },
          })

          setCreatedUserInfo({ username: values.username, pin: formPin ?? '' })
          setStatus({
            success: true,
            message: t('createUserForm.status.success'),
          })
          resetForm()
          handleReset()
          if (onSuccess) onSuccess()
        } catch (error: any) {
          const message = error.message || t('inviteUser.error.sendInvitation')
          setStatus({ success: false, message })
        }
      }}
    >
      {({
        status,
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        resetForm,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Stack spacing={4}>
            <Stack direction="row">
              <Typography variant="h3">{t('createUserForm.title')}</Typography>
              <Tooltip title={t('createUserForm.tooltip')}>
                <IconButton size="small">
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {status?.message && (
              <Alert severity={status.success ? 'success' : 'warning'}>
                {status.message}
              </Alert>
            )}
            <TextField
              name="firstName"
              label={t('createUserForm.firstName.label')}
              value={values.firstName}
              error={Boolean(touched.firstName && errors.firstName)}
              fullWidth
              helperText={touched.firstName && errors.firstName}
              onBlur={handleBlur}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              name="lastName"
              label={t('createUserForm.lastName.label')}
              value={values.lastName}
              error={Boolean(touched.lastName && errors.lastName)}
              fullWidth
              helperText={touched.lastName && errors.lastName}
              onBlur={handleBlur}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              type="username"
              name="username"
              label={t('createUserForm.username.label')}
              value={values.username}
              error={Boolean(touched.username && errors.username)}
              fullWidth
              helperText={touched.username && errors.username}
              onBlur={handleBlur}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              name="jobTitle"
              label={t('createUserForm.jobTitle.label')}
              value={values.jobTitle}
              error={Boolean(touched.jobTitle && errors.jobTitle)}
              fullWidth
              helperText={touched.jobTitle && errors.jobTitle}
              onBlur={handleBlur}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <FormControl
              fullWidth
              error={Boolean(touched.groupIds && errors.groupIds)}
              required
              sx={{ mb: 2 }}
            >
              <InputLabel id="groupIds-select-label">
                {t('createUserForm.role.label', 'Role')}
              </InputLabel>
              <Select
                labelId="groupIds-select-label"
                name="groupIds"
                value={values.groupIds}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('createUserForm.role.label', 'Role')}
                fullWidth
              >
                {groupsLoading ? (
                  <MenuItem value="" disabled>
                    <CircularProgress size={24} />
                  </MenuItem>
                ) : groupsError ? (
                  <MenuItem value="" disabled>
                    <Typography color="error" paddingLeft={2}>
                      {groupsError.message}
                    </Typography>
                  </MenuItem>
                ) : (
                  groups.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      {LocalizationHelpers.localizedGroupName(t, group.name)}
                    </MenuItem>
                  ))
                )}
              </Select>
              {touched.groupIds && errors.groupIds && (
                <FormHelperText>{errors.groupIds}</FormHelperText>
              )}
            </FormControl>
            {values.groupIds !== adminGroup?.id && (
              <RemoteSearchableSelect
                wrapTags
                query={CommonFilters.StoreFilter(t).options.query}
                getOptions={CommonFilters.StoreFilter(t).options.getOptions}
                getVariablesForQuery={
                  CommonFilters.StoreFilter(t).options.getVariablesForQuery
                }
                handleChange={newValue => setSelectedStores(newValue)}
                value={selectedStores}
                label={t('createUserForm.assignedStores.label')}
                multiselect
              />
            )}
            <PinInputField
              value={formPin ?? uniquePin}
              onChange={setFormPin}
              onValidityChange={setIsPinValid}
              required
            />
            {createdUserInfo && (
              <Paper
                elevation={3}
                sx={theme => ({
                  position: 'relative',
                  mt: 2,
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.card,
                  width: '300px',
                })}
              >
                <IconButton
                  size="small"
                  onClick={() => setCreatedUserInfo(null)}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <Close />
                </IconButton>

                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center">
                    <CheckCircle
                      sx={{
                        color: Colors.GREEN_1,
                        marginRight: 1,
                      }}
                    />
                    <Typography variant="h6">
                      {t('createUserForm.status.success')}
                    </Typography>
                  </Stack>
                  <CopyableField
                    label="Username"
                    value={createdUserInfo?.username}
                  />
                  <CopyableField
                    label={t('createUserForm.copyableField.pinLabel')}
                    value={createdUserInfo?.pin}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t('createUserForm.info.shareInformation')}
                  </Typography>
                </Stack>
              </Paper>
            )}
            <Stack direction={'row'} spacing={2} justifyContent={'flex-end'}>
              {onCancel && (
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    resetForm()
                    handleReset()
                    onCancel()
                  }}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || !isPinValid}
              >
                {isSubmitting
                  ? t('createUserForm.status.creatingUser')
                  : t('createUserForm.submit.text')}
              </Button>
              {otherButtons?.map((button, index) => (
                <Button key={index} {...button} />
              ))}
            </Stack>
          </Stack>
        </form>
      )}
    </TypedFormik>
  )
}

export default CreateUser
